// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { AlertUtils } from '@shared/utils/Alert/AlertUtils';
import { TerceraChartTradingOperation } from '../Utils/ChartConstants';
import { TerceraChartHistoryType } from '../TerceraChartUtils';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { DynProperty } from '@shared/commons/DynProperty';
import { AlertNotification, AlertImportance, AlertAfterExecute, getAlertAfterExecuteKey, getAlertImportanceKey } from '@shared/utils/Alert/AlertConstants';
import { Resources } from '@shared/localizations/Resources';
import { TerceraChartCashItemSeriesDataType } from '../Series/TerceraChartCashItemSeriesEnums';
import { SolidBrush } from '@shared/commons/Graphics';
import { LayersEnum } from '../Utils/LayersEnum';
import { createContextMenuItemsFromEnum } from '@shared/utils/ContextMenuUtils';
export class TerceraChartNewAlertRenderer extends TerceraChartBaseRenderer {
    constructor(chart) {
        super(chart);
        this.alertRect = Rectangle.Empty();
        this.selectionRect = Rectangle.Empty();
        this.curInstrument = null;
        this.chartHistoryType = null;
        this.selectedPrice = NaN;
        // bool AllowAddAlert => AlertsManager.Instance.Count < 100;
        this.assignLayer = LayersEnum.CrossHair;
        this.NotificationVariable = AlertNotification.SHOW_POP_UP + AlertNotification.SOUND;
        this.ImportanceVariable = AlertImportance.MEDIUM;
        this.AfterExecuteVariable = AlertAfterExecute.REMOVE;
        this.SetClassName('TerceraChartNewAlertRenderer');
    }
    Draw(gr, window, windowsContainer, advParams) {
        if (this.chart == null || advParams == null) {
            return;
        }
        if (!this.Visible || window == null) {
            this.alertRect = Rectangle.Empty();
            return;
        }
        if (!this.chart.TerceraChartTradingToolsRenderer.ShowAlerts) {
            return;
        }
        this.curInstrument = advParams.Instrument;
        this.chartHistoryType = advParams.TerceraChart.ChartHistoryType();
        const coordinates = advParams.CursorPosition;
        const windowClientRect = window.ClientRectangle;
        const alertSize = 22;
        const alertWithRightPadding = alertSize + 1;
        const newAlertRectX = windowClientRect.X + windowClientRect.Width - alertWithRightPadding;
        let selectionRect = this.selectionRect;
        selectionRect = new Rectangle(newAlertRectX, windowClientRect.Y, alertWithRightPadding + window.PaddingRight, windowClientRect.Height - alertSize);
        if (coordinates != null) {
            if (selectionRect.Contains(coordinates.X, coordinates.Y)) {
                const alertImage = TerceraChartNewAlertRenderer.chartAlertDefaultImage.image;
                let backgroundBrush = new SolidBrush(ThemeManager.colors.chartMediumAlertBackground);
                if (!this.isExistPrice()) {
                    backgroundBrush = new SolidBrush(ThemeManager.colors.chartMediumAlertDisabledBackground);
                }
                else if (this.alertRect.Contains(coordinates.X, coordinates.Y)) {
                    backgroundBrush = new SolidBrush(ThemeManager.colors.chartMediumAlertHoveredBackground);
                }
                this.selectedPrice = this.GetPriceFromCoordY(advParams.HoverWindowYValue, window, advParams.TerceraChart.cashItemSeriesSettings);
                const rectX = windowClientRect.X + windowClientRect.Width - alertWithRightPadding - 1;
                const rectY = coordinates.Y - alertSize / 2;
                this.alertRect = new Rectangle(rectX, rectY, alertSize, alertSize);
                gr.RoundRectangle(this.alertRect, 4, backgroundBrush);
                gr.DrawCenteredImage(alertImage, this.alertRect);
            }
            else {
                this.alertRect = Rectangle.Empty();
                this.selectedPrice = NaN;
            }
        }
    }
    GetPriceFromCoordY(hoverWindowYValue, window, cashItemSeriesSettings) {
        if (!isNullOrUndefined(cashItemSeriesSettings)) {
            const dataType = window.IsMainWindow
                ? cashItemSeriesSettings.DataType
                : TerceraChartCashItemSeriesDataType.Absolute;
            switch (dataType) {
                case TerceraChartCashItemSeriesDataType.Absolute:
                    return this.curInstrument.roundPrice(hoverWindowYValue);
                case TerceraChartCashItemSeriesDataType.Relative:
                case TerceraChartCashItemSeriesDataType.Log:
                    return this.curInstrument.roundPrice(cashItemSeriesSettings.RevertConverter(hoverWindowYValue));
            }
        }
        return NaN;
    }
    ProcessMouseDown(e) {
        if (!this.alertRect.Contains(e.Location.X, e.Location.Y) ||
            this.curInstrument === null) // || this.selectedPrice < this.curInstrument.PointSize)
         {
            return false;
        }
        const ht = TerceraChartHistoryType.GetOriginalHistoryType(this.chartHistoryType, this.curInstrument);
        const valueFromInstrument = AlertUtils.GetCorrectPriceForAlertByInstrument(this.curInstrument, ht);
        if (isNaN(valueFromInstrument)) {
            return false;
        }
        const historyType = TerceraChartHistoryType.GetOriginalHistoryType(this.chartHistoryType, this.curInstrument);
        this.chart.terceraChartPanelContext.ChartVisualTrading(null, {
            action: TerceraChartTradingOperation.ChartNewAlert,
            Account: this.chart.Account(),
            instrument: this.curInstrument,
            value: this.selectedPrice,
            historyType,
            Importance: this.ImportanceVariable,
            AfterExecute: this.AfterExecuteVariable,
            // AlertAction: this.ActionVariable,
            Notification: AlertUtils.VerifyNotificationValue(this.NotificationVariable)
        });
        return true;
    }
    Properties() {
        const properties = super.Properties();
        let prop = new DynProperty('Notification', this.NotificationVariable, DynProperty.ALERT_NOTIFICATION_SELECTOR, DynProperty.ALERTS_GROUP);
        prop.sortIndex = 2;
        prop.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(prop);
        prop = new DynProperty('Importance', this.ImportanceVariable, DynProperty.COMBOBOX, DynProperty.ALERTS_GROUP);
        prop.sortIndex = 3;
        prop.objectVariants = createContextMenuItemsFromEnum(AlertImportance, getAlertImportanceKey);
        prop.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(prop);
        prop = new DynProperty('AfterExecute', this.AfterExecuteVariable, DynProperty.COMBOBOX, DynProperty.ALERTS_GROUP);
        prop.sortIndex = 4;
        prop.objectVariants = createContextMenuItemsFromEnum(AlertAfterExecute, getAlertAfterExecuteKey);
        prop.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(prop);
        return properties;
    }
    callBack(properties) {
        super.callBack(properties);
        let dp = DynProperty.getPropertyByName(properties, 'Notification');
        if (dp != null) {
            this.NotificationVariable = dp.value;
        }
        dp = DynProperty.getPropertyByName(properties, 'AfterExecute');
        if (dp != null) {
            this.AfterExecuteVariable = dp.value;
        }
        dp = DynProperty.getPropertyByName(properties, 'Importance');
        if (dp != null) {
            this.ImportanceVariable = dp.value;
        }
    }
    ThemeChanged() {
        super.ThemeChanged();
        TerceraChartNewAlertRenderer.chartAlertDefaultImage = ThemeManager.NewImages.alertIconDefault;
    }
    GetCursor(e) {
        if (this.alertRect.Contains(e.Location.X, e.Location.Y)) {
            return Cursors.Hand;
        }
        return null;
    }
    ProcessMouseMove(e) {
        // if (this.alertRect.Contains(e.Location.X, e.Location.Y))     // commented out due to the issue described in #114239
        e.NeedRedraw = [LayersEnum.Tools, LayersEnum.CrossHair];
        return super.ProcessMouseMove(e);
    }
    GetTooltip(e) {
        const tooltip = super.GetTooltip(e);
        if (tooltip != null) {
            return tooltip;
        }
        if (!this.alertRect.Contains(e.Location.X, e.Location.Y)) {
            return null;
        }
        if (!this.isExistPrice()) {
            return Resources.getResource('property.NoPriceForCurrentAlertType');
        }
        return null;
    }
    isExistPrice() {
        const historyType = TerceraChartHistoryType.GetOriginalHistoryType(this.chartHistoryType, this.curInstrument);
        const valueFromInstrument = AlertUtils.GetCorrectPriceForAlertByInstrument(this.curInstrument, historyType);
        return isValidNumber(valueFromInstrument);
    }
}
